import { useEffect, useState } from "react";
import {
  createInventoryCategory,
  listInventoryCategories,
  updateInventoryCategory,
  type AdminSession,
  type InventoryCategoryItem,
} from "@/lib/api";
import Card from "../components/Card";

type CategoryForm = {
  name: string;
  active: boolean;
};

const emptyForm: CategoryForm = { name: "", active: true };

export default function CategoriasSection({ session }: { session: AdminSession }) {
  const [categories, setCategories] = useState<InventoryCategoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<CategoryForm>(emptyForm);

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const data = await listInventoryCategories(currentSession);
      setCategories(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar categorías");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  function startEdit(category: InventoryCategoryItem): void {
    setEditingId(category.id);
    setForm({ name: category.name, active: category.active });
  }

  function cancelEdit(): void {
    setEditingId("");
    setForm(emptyForm);
  }

  async function handleSubmit(): Promise<void> {
    if (!form.name.trim()) {
      setError("Ingresa el nombre de la categoría.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId) {
        await updateInventoryCategory(session, editingId, { name: form.name.trim(), active: form.active });
      } else {
        await createInventoryCategory(session, { name: form.name.trim(), active: form.active });
      }
      cancelEdit();
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo guardar la categoría");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {error ? (
        <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="lg:col-span-1">
        <Card title={editingId ? "Editar Categoría" : "Nueva Categoría"}>
          <div className="space-y-2">
            <input
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              placeholder="Nombre de categoría"
            />
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
              />
              Categoría activa
            </label>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {editingId ? (
              <button onClick={cancelEdit} className="rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700">
                Cancelar
              </button>
            ) : null}
            <button disabled={saving} onClick={() => void handleSubmit()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">
              {editingId ? "Guardar cambios" : "Crear"}
            </button>
          </div>
        </Card>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Categorías</h2>
            <span className="text-xs text-gray-500">{categories.length} registros</span>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Cargando categorías...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-semibold uppercase text-gray-500">
                    <th className="px-3 py-2">Nombre</th>
                    <th className="px-3 py-2">Estado</th>
                    <th className="px-3 py-2">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => (
                    <tr key={category.id} className="border-b border-gray-50 text-sm text-gray-700">
                      <td className="px-3 py-2">{category.name}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full px-2 py-1 text-xs ${category.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-700"}`}
                        >
                          {category.active ? "Activa" : "Inactiva"}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <button onClick={() => startEdit(category)} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                  {categories.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-3 py-4 text-sm text-gray-500">
                        No hay categorías registradas.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
